import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Divider,
  Grid,
  IconButton,
  TextField,
  ThemeProvider,
  Toolbar,
  Typography,
  createTheme,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { CreateClienteData } from "../../../types/clientData";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  createClient,
  fetchClientByDocument,
  fetchClientByDocumentApiConfig,
} from "../../../api/clientApi";
import { formatDocument, formatPhone } from "../../../services/formatDocument";
import { LoadingButton } from "@mui/lab";
import {
  subscribeToChannel,
  triggerEvent,
} from "../../../services/pusherService";

interface PusherData {
  NOME_CLIENTE: string;
  TELEFONE_CONTATO_CLIENTE?: string;
  DOCUMENTO_IDENTIFICADOR_CLIENTE?: number | string;
}

const defaultTheme = createTheme({
  typography: {
    fontFamily: "Poppins, sans-serif",
  },
});

const styles = {
  formControl: {
    fullWidth: true,
    marginTop: -2,
    "& .MuiSelect-root": {
      borderColor: "#303030",
      borderRadius: 3,
      boxShadow: 6,
      "&:focus": {
        backgroundColor: "#ffddec",
        borderColor: "brown",
      },
      "&:before": {
        borderColor: "orange",
      },
      "&:after": {
        borderColor: "green",
      },
    },
    ".MuiOutlinedInput-notchedOutline": {
      borderColor: "#303030",
      borderRadius: 3,
      boxShadow: 6,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#303030",
      color: "#303030",
      borderWidth: "2px",
    },
  },
  button: {
    height: 50,
    marginTop: 6,
    backgroundColor: "#303030",
    borderRadius: 2,
    alignItems: "center",
    ":hover": {
      bgcolor: "#5c5c5c",
    },
  },
};

interface FormValues {
  [key: string]: string;
}

const fieldData = [
  {
    label: "CPF/CNPJ",
    name: "documento_identificador_cliente",
    placeholder: "Digite o documento",
  },
  { label: "Nome", name: "nome_cliente", placeholder: "Digite o nome" },
  {
    label: "Telefone de Contato",
    name: "telefone_contato_cliente",
    placeholder: "Digite o telefone",
  },
];

const AddClient: React.FC = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const canal = localStorage.getItem("canal");
  const [loading, setLoading] = useState(false);
  const { companyId } = useParams<{ companyId: string }>();
  const [formValues, setFormValues] = useState<FormValues>({
    nome_cliente: "",
    documento_identificador_cliente: "",
    telefone_contato_cliente: "",
  });
  const [fieldsCompleted, setFieldsCompleted] = useState(false);
  const [fieldsDisabled, setFieldsDisabled] = useState(false);
  const [hasApiConfig, setHasApiConfig] = useState(false);

  useEffect(() => {
    const canal = localStorage.getItem("canal");
    const eventsString = localStorage.getItem("events");
    const events = eventsString ? JSON.parse(eventsString) : [];

    const hasClientEvent = events.some(
      (event: any) => event.categoria === "cliente",
    );

    if (canal && hasClientEvent) {
      setFieldsDisabled(true);
    } else {
      setFieldsDisabled(false);
    }
  }, []);

  useEffect(() => {
    const userDataString = localStorage.getItem("userData");
    if (userDataString) {
      try {
        const parsed = JSON.parse(userDataString);
        if (parsed.ApiConfig) {
          setHasApiConfig(true);
        }
      } catch (e) {
        console.error("Erro ao ler userData:", e);
      }
    }
  }, []);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    fieldName: string,
  ) => {
    const { name, value } = event.target;

    let formattedValue =
      name === "documento_identificador_cliente"
        ? formatDocument(value)
        : name === "telefone_contato_cliente"
          ? formatPhone(value)
          : value;

    if (name === "telefone_contato_cliente") {
      formattedValue = formattedValue.slice(0, 14);
    }

    setFormValues((prevValues) => ({
      ...prevValues,
      [fieldName]: formattedValue,
    }));

    const allFieldsCompleted = Object.values(formValues).every(
      (value) => value.trim() !== "",
    );
    setFieldsCompleted(allFieldsCompleted);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    try {
      if (!token) {
        throw new Error("Token de autenticação não está disponível.");
      }

      if (!fieldsCompleted && !Object.values(formValues).every((v) => v.trim() !== "")) {
        toast.error("Por favor, preencha todos os campos.");
        return;
      }
      const companyIdValue = companyId ?? "";
      const data: CreateClienteData = {
        nome_cliente: formValues.nome_cliente,
        documento_identificador_cliente:
          formValues.documento_identificador_cliente,
        telefone_contato_cliente: formValues.telefone_contato_cliente,
        id_empresa: parseInt(companyIdValue),
      };

      await createClient(token, data);
      toast.success("Cliente criado com sucesso!");
      navigate("/cliente");
    } catch (error) {
      toast.error("Erro ao cadastrar cliente.");
    } finally {
      setLoading(false);
    }
  };

  const removeSpecialCharacters = (value: string) => {
    return value.replace(/[.\-/]/g, "");
  };

  useEffect(() => {
    const documento = formValues.documento_identificador_cliente;
    const documentoLimpo = removeSpecialCharacters(documento);
    const eventsString = localStorage.getItem("events");
    const events = eventsString ? JSON.parse(eventsString) : [];
    const hasClientEvent = events.some(
      (event: any) => event.categoria === "cliente",
    );

    if (
      (documentoLimpo.length === 11 || documentoLimpo.length === 14) &&
      canal &&
      hasClientEvent
    ) {
      const buscarCliente = async () => {
        try {
          if (!token) {
            throw new Error("Token de autenticação não está disponível.");
          }

          const clientData = await fetchClientByDocument(documentoLimpo, token);

          if (clientData) {
            triggerEvent(canal, "cliente", clientData);
          } else {
            console.warn("fetchClientByDocument vazio/null");
          }
        } catch (error) {}
      };

      buscarCliente();
    }
  }, [formValues.documento_identificador_cliente, token, canal]);

  useEffect(() => {
    if (!hasApiConfig) return;
    if (!token) return;

    const documento = formValues.documento_identificador_cliente;
    if (!documento) return;

    const documentoLimpo = removeSpecialCharacters(documento);

    if (documentoLimpo.length !== 11 && documentoLimpo.length !== 14) {
      return;
    }

    const buscarClienteApiConfig = async () => {
      try {
        const data = await fetchClientByDocumentApiConfig(
          documentoLimpo,
          token,
        );

        if (!data) return;

        setFormValues((prev) => ({
          ...prev,
          documento_identificador_cliente: formatDocument(
            data.documento_identificador_cliente || documentoLimpo,
          ),
          nome_cliente: data.nome_cliente || prev.nome_cliente,
          telefone_contato_cliente: data.telefone_contato_cliente
            ? formatPhone(data.telefone_contato_cliente)
            : prev.telefone_contato_cliente,
        }));
      } catch (error) {
        console.error("Erro ao buscar cliente via ApiConfig:", error);
        toast.error("Erro ao buscar cliente na API.");
      }
    };

    buscarClienteApiConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues.documento_identificador_cliente, hasApiConfig, token]);

  useEffect(() => {
    if (!canal) {
      return;
    }

    const unsubscribe = subscribeToChannel(canal, "cliente", (data: any) => {
      try {
        let parsedData: PusherData;
        if (typeof data === "string") {
          parsedData = JSON.parse(data) as PusherData;
        } else {
          parsedData = data;
        }

        if (parsedData) {
          setFormValues((prevValues) => ({
            ...prevValues,
            nome_cliente: parsedData.NOME_CLIENTE || prevValues.nome_cliente,
            telefone_contato_cliente:
              formatPhone(parsedData.TELEFONE_CONTATO_CLIENTE?.trim() ?? "") ||
              prevValues.telefone_contato_cliente,
            documento_identificador_cliente:
              parsedData.DOCUMENTO_IDENTIFICADOR_CLIENTE
                ? formatDocument(
                    String(parsedData.DOCUMENTO_IDENTIFICADOR_CLIENTE),
                  )
                : prevValues.documento_identificador_cliente,
          }));
        } else {
          console.warn("Dados recebidos mas vazios ou inválidos");
        }
      } catch (error) {
        console.error("Erro ao processar dados do Pusher:", {
          error,
          tipoDadosRecebidos: typeof data,
          dadosRecebidos: data,
          stringifyResult: JSON.stringify(data),
        });
      }
    });

    return () => {
      unsubscribe();
    };
  }, [canal]);

  return (
    <ThemeProvider theme={defaultTheme}>
      <Box sx={{ display: "flex" }}>
        <Box
          sx={{
            flexGrow: 1,
            overflow: "auto",
          }}
        >
          <Toolbar sx={{ mb: 2, mt: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                "&:hover": {
                  cursor: "pointer",
                  color: "red",
                },
              }}
              onClick={() => navigate("/cliente")}
            >
              <IconButton
                edge="start"
                color="inherit"
                aria-label="voltar"
                sx={{ color: "inherit" }}
              >
                <ArrowBack />
              </IconButton>
              <Typography
                variant="h6"
                component="div"
                sx={{ color: "inherit", mt: 0.2 }}
              >
                Voltar
              </Typography>
            </Box>
          </Toolbar>

          <Container sx={{ mb: 4 }} maxWidth={"lg"}>
            <Box component="form" noValidate>
              <Typography
                variant="h5"
                fontStyle={"inherit"}
                sx={{ fontWeight: 400 }}
              >
                Adicionar Cliente
                <Divider
                  variant="middle"
                  sx={{
                    width: 120,
                    backgroundColor: "red",
                    height: 2,
                    marginLeft: 0,
                  }}
                />
              </Typography>
              <Grid container spacing={3} mt={-2} p={2} sx={styles.formControl}>
                {fieldData.map((field) => (
                  <Grid item xs={12} sm={6} key={field.name}>
                    <Typography ml={1} mb={-1}>
                      {field.label}
                      <Typography component="span" color="error">
                        *
                      </Typography>
                    </Typography>
                    <TextField
                      fullWidth
                      margin="normal"
                      placeholder={field.placeholder}
                      name={field.name}
                      value={formValues[field.name]}
                      onChange={(e) => handleChange(e, field.name)}
                      required
                      disabled={
                        fieldsDisabled &&
                        (field.name === "nome_cliente" ||
                          field.name === "telefone_contato_cliente")
                      }
                      sx={{
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#303030",
                        },
                        "& .MuiInputLabel-root": {
                          borderColor: "#303030",
                        },
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 3,
                          boxShadow: 6,
                          "& fieldset": {
                            borderColor: "#303030",
                          },
                        },
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                          {
                            borderColor: "#303030",
                          },
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
              <LoadingButton
                variant="contained"
                fullWidth
                sx={styles.button}
                loading={loading}
                onClick={handleSubmit}
              >
                <Typography>Adicionar</Typography>
              </LoadingButton>
            </Box>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default AddClient;
